/**
 * SpreadsTotals — moneyline-style table view for spread + total markets.
 * One row per canonical game expanded to 4 perspective rows (away spread,
 * home spread, over total, under total). See SpreadsTotalsTable for the
 * column structure and ladder convention.
 *
 * Owns:
 *   - Per-event selected line identities for spread + total chip strips
 *   - Which single row (if any) is currently expanded as a ladder, across
 *     all events — one nullable {eventKey, rowKind}, so "two events open
 *     at once" is unrepresentable rather than merely discouraged (#219)
 *   - Orderbook subscription needs (only the selected line of the expanded
 *     row per event, so we don't subscribe every line on every game)
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMarketStore } from '@/stores/marketStore';
import { useAccountStore } from '@/stores/accountStore';
import { useUserKalshiPOS } from '@/hooks/useUserKalshiPOS';
import { useGameStatesPoll } from '@/hooks/useGameStatesPoll';
import { toHydraGameKeyLive } from '@/services/analyticsGameKey';
import type {
  GameEventRow,
  GameRow,
  Sport,
  StatusFilter,
  DateFilter,
  SlateRange,
  MarketSide,
  PriceMode,
  SpreadLine,
  TotalLine,
  TradeTicketContext,
} from '../ValueDashboard/types';
import type { BetLineContext } from '../ValueDashboard/GameDetailPanel';
import {
  lt2CardId,
  LT2_DASH_CARD_SCOPE,
  SpreadsTotalsTable,
  type ExpandedRow,
  type PillTradeMeta,
  type RowKind,
} from './SpreadsTotalsTable';
import {
  filterEventExecutableLines,
  linePriceCents,
  nearestLineWindow,
  spreadLineKey,
  synthesizeFromSpread,
  synthesizeFromTotal,
  totalLineKey,
} from './synthesize';
import type { CfbBucket } from '@/lib/cfb/subdivision';
import { cfbRowMatchesBucket } from '@/lib/cfb/subdivision';
import { useDisplaySettings } from '@/stores/displaySettings';
import { pickUpcomingSlate, slateScopeCopy, upcomingSlateCopy } from '../ValueDashboard/upcomingSlate';
import { slateWindowContains, startOfIsoDayMs } from '../ValueDashboard/weeklySlate';
import { useLt2Access } from '@/hooks/useLt2Access';
import { useLt2FocusStore } from '@/stores/lt2FocusStore';
import { buildBetLineContext } from './buildBetLineContext';
import { findSpreadTotalFocusMatch } from '../GameDetail/SpreadTotalSection';
import { focusEventIdentityMatches } from '@/lib/routing/focusedDashboard';
import type { ExternalFocusResolution } from '../ValueDashboard/useFocusedDashboardHydration';

interface SpreadsTotalsProps {
  sport: Sport;
  /** #653 — narrow a CFB board to one subdivision bucket. The dashboard fetches
   *  moneyline rows and filters them by board key upstream, but this board owns
   *  its own `getGameEvents` read, so the bucket has to arrive as a prop or the
   *  chips would look active here while doing nothing. Absent (every non-CFB
   *  board, and CFB's own "All CFB") means show the whole sport. */
  cfbBucket?: CfbBucket | null;
  /** The board's own name when a bucket narrows it ("CFB · FBS"), for the
   *  table title. Absent keeps the title deriving from the rows' sport. */
  boardLabel?: string;
  statusFilter: StatusFilter;
  dateFilter: DateFilter;
  /** First and last day of the board's bounded view, both inclusive. Omitting
   *  `rangeEndIso` means a single day — see the weekly slate in
   *  ValueDashboard/weeklySlate.ts (#712). */
  selectedDateIso: string;
  rangeEndIso?: string;
  /** Day or Week — the SHAPE of that window, which the pair of dates above
   *  cannot state on its own (a week whose games all fall on one day is
   *  indistinguishable from a day). #757's fallback needs it: a weekly board
   *  falls forward a whole week, not a day. */
  slateRange?: SlateRange;
  /** #757/#248 — a focused deep link is pinning the date, so this board stays
   *  exact-date and never falls forward: a stale link must not appear to land
   *  on a neighbouring event. Only the dashboard can see a focus query, so it
   *  travels as a prop; the other half of the question — whether the view is
   *  bounded by a date at all — is already in `dateFilter` above. */
  datePinnedByFocus?: boolean;
  /** Toolbar YES/NO toggle — drives pill highlight and ladder side. */
  marketSide: MarketSide;
  /** Toolbar Maker/Taker/Market toggle — drives the collapsed pill price.
   *  Pills read via `pillPriceCents(book, side, priceMode)` to match the
   *  moneyline GameTable behaviour. */
  priceMode: PriceMode;
  /**
   * Fired with the synthesized GameRow whenever a spread or total row is
   * expanded, or null when it collapses. Parent uses it to render the
   * right-side detail panel.
   */
  onActiveGameChange?: (game: GameRow | null) => void;
  /**
   * Fired alongside `onActiveGameChange` with the bet-description title +
   * chip strip context, so the detail panel can render the alternate
   * header for spread/total bets. Null when no row is expanded.
   */
  onActiveBetLineContextChange?: (context: BetLineContext | null) => void;
  /**
   * Fired when the user clicks an individual Y/N pill — parent seeds the
   * side panel's order ticket (GameDetailPanel tradeIntent).
   */
  onTradeClick?: (data: TradeTicketContext) => void;
  /**
   * Counter that bumps when the parent's detail panel closes. Tells this
   * view to collapse its inline expansion state so re-opening the panel
   * doesn't require two clicks.
   */
  closeSignal?: number;
  /**
   * #539 — the exact market ticker a focused deep link names, when that link
   * targets a spread or total. This board resolves it rather than the parent
   * because it already holds `getGameEvents` rows memoized, and that selector
   * (unlike `getGames`) is uncached — re-deriving it upstream would rebuild
   * every event on every feed tick.
   *
   * Modelled on `GameDetail`'s `SpreadTotalSection`, which takes the same
   * `focusTicker` / `focusSide` pair — and reuses its exported matcher. The two
   * are NOT identical: this board additionally honours the link's event and
   * venue (below), because it searches MANY events where that section is
   * already scoped to the one it renders.
   */
  focusTicker?: string | null;
  /** The event the link claims. Honoured so a link whose event and ticker
   *  contradict each other resolves to nothing instead of opening the ticker
   *  anyway. */
  focusEventIdentity?: string | null;
  /** The venue the link named. Honoured for the same reason the event is. */
  focusVenue?: 'kalshi' | 'polymarket' | null;
  /** YES/NO side the focused link named. Seeds the ticket's order side. */
  focusSide?: MarketSide;
  /**
   * Reports resolution back to the hydration state machine, which owns the
   * one-shot, the connected-timeout, and the unavailable notice. Fires
   * `searching` until the line arrives (the row may still be in flight, so
   * that is a WAIT, not a verdict) and `opened` once the panel is open.
   */
  onFocusResolution?: (resolution: ExternalFocusResolution) => void;
}

/** Which line on which event a focus ticker names. `idx` is resolved from the
 *  ticker at open time and never stored as the identity — the identity is the
 *  ticker itself, which is what `findSpreadTotalFocusMatch` matches on. */
export interface SpreadsTotalsFocusMatch {
  eventKey: string;
  rowKind: RowKind;
  idx: number;
  /** Which venue's book carried the exact ticker. Carried through so the panel
   *  opens on the venue the link named rather than the board's default. */
  venue: 'kalshi' | 'polymarket';
}

/** Locate a focus ticker across every event row on the board.
 *
 *  `GameDetail` resolves a focus within ONE event it already has; the board has
 *  many, so the event scan is the only genuinely new step — the per-event line
 *  match reuses `findSpreadTotalFocusMatch` verbatim rather than restating it.
 *
 *  `eventIdentity` is REQUIRED to be honoured when the caller supplies it, and
 *  that is not redundant with the ticker. A ticker alone would open any market
 *  it names regardless of which event the link claimed — so a link whose event
 *  and ticker CONTRADICT each other would still open something, and the
 *  ticket's "contradictory identity shows the unavailable state" criterion
 *  would be unmet. Constraining the scan first makes a contradiction resolve to
 *  nothing, which the caller reports as still-searching and the hydration
 *  timeout turns into the notice. (It is the moneyline matcher's rule too —
 *  imported rather than restated, so the two boards cannot disagree.)
 *
 *  Fail-closed by construction otherwise: the reused helper compares exact
 *  market tickers, so a ticker absent from every line returns null (keep
 *  waiting) rather than resolving to a nearby line. Exported for the #539
 *  tests. */
export function findBoardFocusMatch(
  rows: readonly GameEventRow[],
  focusTicker?: string | null,
  eventIdentity?: string | null,
  venue?: 'kalshi' | 'polymarket' | null,
): SpreadsTotalsFocusMatch | null {
  if (!focusTicker) return null;
  for (const row of rows) {
    if (eventIdentity && !focusEventIdentityMatches(eventIdentity, row)) continue;
    const match = findSpreadTotalFocusMatch(row, focusTicker);
    if (!match) continue;
    // The moneyline matcher restricts its search to the requested venue; this
    // path must too, or the two disagree about what a contradictory link means.
    // A ticker belongs to one venue in practice, so this rarely bites — but
    // "rarely" is not the standard for a link that opens an order ticket.
    if (venue && match.venue !== venue) continue;
    return {
      eventKey: row.key,
      rowKind: match.rowKind,
      idx: match.idx,
      venue: match.venue,
    };
  }
  return null;
}

/** When an event sorts — its scheduled start, or midnight on its own date when
 *  the feed hasn't stamped one, and last of all when it has neither. Mirrors
 *  the moneyline board's `gameSortMs`. Sorting last is NOT a filter: see
 *  `pickUpcomingSlate` on why callers owe it dateable rows. */
function eventSortMs(event: GameEventRow): number {
  if (event.startTimeMs != null && Number.isFinite(event.startTimeMs)) return event.startTimeMs;
  const dayMs = event.date ? startOfIsoDayMs(event.date) : Number.NaN;
  return Number.isFinite(dayMs) ? dayMs : Number.POSITIVE_INFINITY;
}

/** Index of the line whose primary venue YES priceCents is closest to 50¢, or
 *  null when NOT ONE line carries a price yet — an empty strip included.
 *
 *  #780: the two answers used to be spelled the same way (`0`), which is why
 *  a row seeded before its books were priced froze on the end of the ladder.
 *  "The first line is closest to 50¢" and "nothing is priced, ask again
 *  later" are different facts, so callers get to tell them apart. */
export function nearestToFiftyIndex(
  lines: Array<SpreadLine | TotalLine>,
): number | null {
  let bestIdx: number | null = null;
  let bestDist = Infinity;
  for (let i = 0; i < lines.length; i++) {
    const cents = linePriceCents(lines[i]);
    if (cents == null) continue;
    const dist = Math.abs(cents - 50);
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/** Index of the line whose primary venue YES priceCents is closest to 50¢.
 *  Determines the *default* chip when the strip first renders for a game;
 *  the user can navigate to any other line. With nothing priced the first
 *  line is all the board can show — a RENDERED fallback only, never a
 *  selection worth remembering (see `seedLineKeys`). */
export function defaultLineIndex(
  lines: Array<SpreadLine | TotalLine>,
): number {
  return nearestToFiftyIndex(lines) ?? 0;
}

export function SpreadsTotals({
  sport,
  cfbBucket,
  boardLabel,
  statusFilter,
  dateFilter,
  selectedDateIso,
  rangeEndIso,
  slateRange = 'day',
  datePinnedByFocus = false,
  marketSide,
  priceMode,
  onActiveGameChange,
  onActiveBetLineContextChange,
  onTradeClick,
  closeSignal = 0,
  focusTicker,
  focusEventIdentity,
  focusVenue,
  focusSide,
  onFocusResolution,
}: SpreadsTotalsProps) {
  const connected = useMarketStore((s) => s.connected);
  const getGameEvents = useMarketStore((s) => s.getGameEvents);
  const version = useMarketStore((s) => s.version);

  // D-020 LT2-005: under lt2, Poly-only lines are hidden from the execution
  // view unless the reveal toggle is on. Filtered at the SOURCE so idx
  // seeding, focus matching, orderbook needs, and the pill-trade line lookup
  // all see one consistent world. lt2 off → arrays untouched.
  const lt2Access = useLt2Access();
  const showPolyOnlyLines = useDisplaySettings((s) => s.spreadTotalShowPolyOnlyLines);
  const hidePolyOnlyLines = lt2Access && !showPolyOnlyLines;

  // Which of the sport's events this board may show, whatever their date.
  // Held apart from the date read so the #757 fallback runs the SAME rule: a
  // bucket or Poly-only filter applied after a date was chosen would land the
  // board on a day whose only rows it then hides.
  const applyBoardFilters = useCallback((fetched: GameEventRow[]): GameEventRow[] => {
    // Filter at the SOURCE, like the Poly-only rule below: line seeding, focus
    // matching, and orderbook subscriptions must all see one world.
    const base = cfbBucket ? fetched.filter((r) => cfbRowMatchesBucket(r, cfbBucket)) : fetched;
    if (!hidePolyOnlyLines) return base;
    return base
      .map((r) => filterEventExecutableLines(r, false))
      .filter((r) => r.spreads.length + r.totals.length > 0);
  }, [cfbBucket, hidePolyOnlyLines]);

  // May an empty window stand aside for the next one with rows? Both halves of
  // the answer are here: the view has to be bounded by a date at all, and no
  // focused deep link may be pinning it.
  const mayFallForward = dateFilter === 'today' && !datePinnedByFocus;

  // The requested window, and — when a fallback is possible — the whole
  // catalog it would be chosen from, in ONE store read.
  //
  // `getGameEvents` is uncached and re-parses every ticker for the sport on
  // every call, so the bounded read costs nearly as much as the unbounded one
  // (~35ms vs ~42ms on an in-season CFB catalog) and the difference is all in
  // rows we may need anyway. Reading both would double the board's per-flush
  // cost in exactly the state this feature exists for — a fallen-forward board
  // is a board being TRADED, with a full table rendering beside it — so take
  // the wider read once and derive the window from it in memory. The date is
  // the event's own (`row.date`), which is what the store filters on.
  const { requestedRows, fallbackCandidates } = useMemo(() => {
    void version;
    if (!mayFallForward) {
      return {
        requestedRows: applyBoardFilters(
          getGameEvents(sport, { statusFilter, dateFilter, selectedDateIso, rangeEndIso }),
        ),
        fallbackCandidates: null,
      };
    }
    const everyDate = applyBoardFilters(getGameEvents(sport, { statusFilter, dateFilter: 'all' }));
    const window = { startIso: selectedDateIso, endIso: rangeEndIso ?? selectedDateIso };
    return {
      // Filtering preserves the store's order, so the board is row-for-row what
      // the bounded read would have returned.
      requestedRows: everyDate.filter((r) => slateWindowContains(window, r.date)),
      fallbackCandidates: everyDate,
    };
  }, [
    getGameEvents,
    sport,
    applyBoardFilters,
    statusFilter,
    dateFilter,
    selectedDateIso,
    rangeEndIso,
    mayFallForward,
    version,
  ]);

  // #757 — the requested window is empty, so stand in the next one that isn't.
  // Chosen from the SAME store snapshot the board already read (no new
  // subscription, poll or fetch); `version` upstream is what un-sticks an early
  // empty render once markets hydrate.
  const fallbackSlate = useMemo(() => {
    if (!fallbackCandidates || requestedRows.length > 0) return null;
    return pickUpcomingSlate({
      rows: fallbackCandidates.filter((r) => r.gameState !== 'final'),
      fromIso: selectedDateIso,
      range: slateRange,
      dateOf: (r) => r.date,
      sortMs: eventSortMs,
      tieBreak: (r) => r.awayCode,
    });
  }, [fallbackCandidates, requestedRows.length, selectedDateIso, slateRange]);

  // The rows ON SCREEN, whichever window they came from. Everything below —
  // line seeding, focus matching, orderbook needs, the game-state poll, pill
  // trades — reads this one name, so the fallback cannot reach the table
  // without reaching them too.
  const rows = fallbackSlate?.rows ?? requestedRows;

  // Batched Unabated gameState poll for the visible event rows. Drives
  // the period status text + per-team scores in MatchupRow once a game
  // starts. Pulls from the same hydra endpoint the moneyline page
  // polls; ValueDashboard already polls when the moneyline tab is up,
  // but the spread/total tab needs its own subscription scoped to its
  // events (which can be a subset/superset of the dashboard view).
  const eventGameKeys = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const r of rows) {
      const k = toHydraGameKeyLive(r.key);
      if (k) set.add(k);
    }
    return Array.from(set);
  }, [rows]);
  useGameStatesPoll(eventGameKeys);

  // Per-event selection is stored by LINE IDENTITY, never array position.
  // Hydra can reorder a strip as books move; retaining an index would silently
  // retarget the trader's focused card and hotkeys to a different market.
  const [spreadKeyByEvent, setSpreadKeyByEvent] = useState<Map<string, string>>(new Map());
  const [totalKeyByEvent, setTotalKeyByEvent] = useState<Map<string, string>>(new Map());
  const spreadIdxByEvent = useMemo(
    () => resolveLineIndices(spreadKeyByEvent, rows, (r) => r.spreads, spreadLineKey),
    [spreadKeyByEvent, rows],
  );
  const totalIdxByEvent = useMemo(
    () => resolveLineIndices(totalKeyByEvent, rows, (r) => r.totals, totalLineKey),
    [totalKeyByEvent, rows],
  );
  // Exactly one expansion across all events — the shape, not writer
  // discipline, is what enforces it. See ExpandedRow in SpreadsTotalsTable.
  const [expandedRow, setExpandedRow] = useState<ExpandedRow>(null);

  // The parent passes inline arrows, so these change identity every render.
  // Held in refs and kept OUT of the selection effect's deps: including them
  // would re-run it on every parent render, and the focus latch below — not
  // dependency stability — is what makes applying a focus idempotent.
  const onTradeClickRef = useRef(onTradeClick);
  const onFocusResolutionRef = useRef(onFocusResolution);
  useEffect(() => {
    onTradeClickRef.current = onTradeClick;
    onFocusResolutionRef.current = onFocusResolution;
  });

  // #539 one-shot, keyed on the TICKER (plus side), never on the resolved
  // index: the index is re-derived from the ticker each pass and is not the
  // identity. Without the latch a focus would re-open the ticket on every feed
  // tick, stamping over a trader who had since moved to another line.
  const appliedFocusRef = useRef<string | null>(null);

  // #608 — when the selected market leaves the feed the card stays mounted
  // under the same id, so nothing unregisters and the held hotkey focus
  // survives while the card falls back to a default line. Drop the focus for
  // exactly that card. A LAYOUT effect so it lands before the passive seed
  // below overwrites the stale key — after that the mismatch is invisible.
  // The visible fallback selection is untouched: refocusing is the trader's
  // explicit act.
  useLayoutEffect(() => {
    const { clearFocusIf } = useLt2FocusStore.getState();
    for (const row of rows) {
      const spreadKey = spreadKeyByEvent.get(row.key);
      if (spreadKey != null && !row.spreads.some((l) => spreadLineKey(l) === spreadKey)) {
        clearFocusIf(lt2CardId(LT2_DASH_CARD_SCOPE.keyPrefix, row.key, 'spread'));
      }
      const totalKey = totalKeyByEvent.get(row.key);
      if (totalKey != null && !row.totals.some((l) => totalLineKey(l) === totalKey)) {
        clearFocusIf(lt2CardId(LT2_DASH_CARD_SCOPE.keyPrefix, row.key, 'total'));
      }
    }
  }, [rows, spreadKeyByEvent, totalKeyByEvent]);

  // Establishes which line is selected for the rows currently on the board:
  // a closest-to-50¢ default for rows that just arrived, then #539's focused
  // deep link on top. Deliberately ONE effect rather than two — the focus must
  // win over the default, and ordering inside a single effect is guaranteed
  // where ordering between two sibling effects is merely conventional.
  useEffect(() => {
    setSpreadKeyByEvent((prev) =>
      seedLineKeys(prev, rows, (r) => r.spreads, spreadLineKey, nearestToFiftyIndex),
    );
    setTotalKeyByEvent((prev) =>
      seedLineKeys(prev, rows, (r) => r.totals, totalLineKey, nearestToFiftyIndex),
    );
    // Drop the expansion only if its event left the feed. Returning `prev`
    // otherwise keeps the reference stable, avoiding an extra state update →
    // re-render → second effect pass when the expansion didn't change.
    setExpandedRow((prev) =>
      prev && !rows.some((r) => r.key === prev.eventKey) ? null : prev,
    );

    // ── #539: a focused deep link naming a spread or total ────────────
    if (!focusTicker) {
      appliedFocusRef.current = null;
      return;
    }
    const applyKey = `${focusTicker.toUpperCase()}:${focusSide ?? ''}`;
    if (appliedFocusRef.current === applyKey) return;

    const match = findBoardFocusMatch(rows, focusTicker, focusEventIdentity, focusVenue);
    const row = match ? rows.find((r) => r.key === match.eventKey) : undefined;
    const line =
      match && row
        ? match.rowKind === 'spread'
          ? row.spreads[match.idx]
          : row.totals[match.idx]
        : undefined;
    // Not found is NOT a verdict — the event or its lines may still be
    // arriving. The hydration hook's connected-timeout is what eventually turns
    // a persistent `searching` into the unavailable notice, exactly as it
    // treats a moneyline miss.
    if (!match || !row || !line) {
      onFocusResolutionRef.current?.('searching');
      return;
    }

    appliedFocusRef.current = applyKey;
    // Applied AFTER the default seeding above, in the same effect, so the
    // focused line overwrites the default rather than racing it. On an
    // unpriced row the seeding writes nothing (#780) and this is the only
    // writer — the ordering matters just as much there, because the key it
    // leaves is what stops the later re-seed from reclaiming the row.
    if (match.rowKind === 'spread') {
      setSpreadKeyByEvent((prev) =>
        new Map(prev).set(row.key, spreadLineKey(line as SpreadLine)),
      );
    } else {
      setTotalKeyByEvent((prev) =>
        new Map(prev).set(row.key, totalLineKey(line as TotalLine)),
      );
    }
    setExpandedRow({ eventKey: row.key, rowKind: match.rowKind });

    // Seeds the ticket through the SAME `onTradeClick` contract a pill click
    // uses — no second order-entry path. The synthesized row's away/home slots
    // reference one shared book, so 'away' is the canonical slot, matching the
    // choice GameDetail's focus path makes. No price is passed: the URL layer
    // never carries one and the panel derives the current book price itself.
    onTradeClickRef.current?.({
      game:
        match.rowKind === 'spread'
          ? synthesizeFromSpread(row, line as SpreadLine)
          : synthesizeFromTotal(row, line as TotalLine),
      teamSide: 'away',
      venue: match.venue,
      initialOrderSide: focusSide ?? marketSide,
      postOnly: priceMode === 'maker',
    });
    onFocusResolutionRef.current?.('opened');
  }, [rows, focusTicker, focusEventIdentity, focusVenue, focusSide, marketSide, priceMode]);

  // Orderbook needs — the currently-selected spread AND total ticker per
  // event, even when collapsed. Pills use `pillPriceCents` which derives
  // best ask from the opposite side's bid stream, mirroring the moneyline
  // GameTable behaviour and matching Kalshi's quick-view ask prices. Cost
  // scales with rendered events × 2 strips × 2 venues, not Σ lines.
  const nearestLinesOn = useDisplaySettings((s) => s.spreadTotalNearestLines);
  const orderbookTickers = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      const spread = row.spreads[spreadIdxByEvent.get(row.key) ?? 0];
      const total = row.totals[totalIdxByEvent.get(row.key) ?? 0];
      if (spread?.kalshi?.marketTicker) set.add(spread.kalshi.marketTicker);
      if (spread?.polymarket?.marketTicker) set.add(spread.polymarket.marketTicker);
      if (total?.kalshi?.marketTicker) set.add(total.kalshi.marketTicker);
      if (total?.polymarket?.marketTicker) set.add(total.polymarket.marketTicker);
    }
    // D-020 LT2-017: the expanded row's nearest-three window needs depth for
    // its neighbor lines too. One extra event × ≤2 tickers — bounded.
    if (nearestLinesOn && expandedRow) {
      const row = rows.find((r) => r.key === expandedRow.eventKey);
      if (row) {
        const lines = expandedRow.rowKind === 'spread' ? row.spreads : row.totals;
        const selected =
          expandedRow.rowKind === 'spread'
            ? spreadIdxByEvent.get(row.key) ?? 0
            : totalIdxByEvent.get(row.key) ?? 0;
        for (const idx of nearestLineWindow(lines.length, selected)) {
          const t = lines[idx]?.kalshi?.marketTicker;
          if (t) set.add(t);
        }
      }
    }
    return Array.from(set);
  }, [rows, spreadIdxByEvent, totalIdxByEvent, nearestLinesOn, expandedRow]);

  const orderbookTickersKey = useMemo(
    () => orderbookTickers.slice().sort().join('|'),
    [orderbookTickers],
  );

  useEffect(() => {
    useMarketStore.getState().setOrderbookNeeds('spreads-totals', orderbookTickers);
    return () => {
      useMarketStore.getState().setOrderbookNeeds('spreads-totals', []);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderbookTickersKey]);

  // Stable references: select arrays directly; the table walks them for
  // per-event strip aggregation. Don't `.map()` inside the selector —
  // that mints a new array every render and infinite-loops.
  const openOrders = useAccountStore((s) => s.openOrders);
  const accountPositions = useAccountStore((s) => s.positions);

  // Snapshot filter is ONLY the user's own tickers (open orders +
  // positions). PositionTracker does the same — keeps Kalshi's
  // /portfolio/orders/queue_positions call narrow, which is the only
  // way queue depth comes back reliably. Pre-spread/total fix, we sent
  // every visible spread/total ticker to the snapshot (hundreds across
  // sports + events) and Kalshi's response silently dropped queue data
  // for some tickers — chips showed `count/?`. C/O ring + position
  // aggregation reads accountStore directly, so they don't need
  // visible tickers in the snapshot.
  const kalshiVisibleTickers = useMemo(() => {
    const set = new Set<string>();
    for (const o of openOrders) if (o.ticker) set.add(o.ticker);
    for (const p of accountPositions) if (p.ticker) set.add(p.ticker);
    return Array.from(set);
  }, [openOrders, accountPositions]);
  const userActivity = useUserKalshiPOS(kalshiVisibleTickers);

  // Chip-strip scrolls implicitly activate the row they're on. Otherwise
  // a trader scrolling chips on row B while row A is still expanded would
  // see the side panel keep rendering A — confusing because the chip
  // they're touching IS visibly highlighting on B. Mirrors the
  // "only one expanded across all events" rule handleToggleExpand
  // enforces: this row's kind becomes the sole expansion.
  const handleSelectSpreadIdx = useCallback((eventKey: string, idx: number) => {
    const line = rows.find((row) => row.key === eventKey)?.spreads[idx];
    if (!line) return;
    setSpreadKeyByEvent((prev) => {
      const next = new Map(prev);
      next.set(eventKey, spreadLineKey(line));
      return next;
    });
    setExpandedRow({ eventKey, rowKind: 'spread' });
  }, [rows]);

  const handleSelectTotalIdx = useCallback((eventKey: string, idx: number) => {
    const line = rows.find((row) => row.key === eventKey)?.totals[idx];
    if (!line) return;
    setTotalKeyByEvent((prev) => {
      const next = new Map(prev);
      next.set(eventKey, totalLineKey(line));
      return next;
    });
    setExpandedRow({ eventKey, rowKind: 'total' });
  }, [rows]);

  const handleToggleExpand = useCallback((eventKey: string, row: RowKind) => {
    // Clicking a spread/total in event B closes whatever was open in event
    // A — the parent detail panel renders a single context. Re-clicking the
    // same row collapses it.
    setExpandedRow((prev) =>
      prev?.eventKey === eventKey && prev.rowKind === row
        ? null
        : { eventKey, rowKind: row },
    );
  }, []);

  // Parent closes its panel → collapse our inline expansion so the next
  // click on the same row re-opens the panel cleanly. Skip the very first
  // run (closeSignal=0 at mount).
  useEffect(() => {
    if (closeSignal === 0) return;
    setExpandedRow(null);
  }, [closeSignal]);


  // Notify parent of the currently-active (event, market-type) → synthesized
  // GameRow so the right-side detail panel can render. Also fires the
  // matching BetLineContext so the panel can render its alternate header
  // (bet-description title + chip strip). Both fire `null` when no row is
  // expanded across any event.
  useEffect(() => {
    let active: GameRow | null = null;
    let context: BetLineContext | null = null;
    // Direct lookup, not a rows-ordered scan: the expanded event IS the one
    // named in state, so the panel can no longer resolve to a different
    // event than the trade (#215).
    const row = expandedRow ? rows.find((r) => r.key === expandedRow.eventKey) : undefined;
    if (row && expandedRow) {
      if (expandedRow.rowKind === 'spread') {
        const idx = spreadIdxByEvent.get(row.key) ?? 0;
        const line = row.spreads[idx];
        if (line) {
          active = synthesizeFromSpread(row, line);
          context = buildBetLineContext({
            kind: 'spread',
            event: row,
            lines: row.spreads,
            selectedIdx: idx,
            onSelectIdx: (next) => handleSelectSpreadIdx(row.key, next),
          });
        }
      } else {
        const idx = totalIdxByEvent.get(row.key) ?? 0;
        const line = row.totals[idx];
        if (line) {
          active = synthesizeFromTotal(row, line);
          context = buildBetLineContext({
            kind: 'total',
            event: row,
            lines: row.totals,
            selectedIdx: idx,
            onSelectIdx: (next) => handleSelectTotalIdx(row.key, next),
          });
        }
      }
    }
    onActiveGameChange?.(active);
    onActiveBetLineContextChange?.(context);
  }, [
    rows,
    expandedRow,
    spreadIdxByEvent,
    totalIdxByEvent,
    onActiveGameChange,
    onActiveBetLineContextChange,
    handleSelectSpreadIdx,
    handleSelectTotalIdx,
  ]);

  const handlePillTrade = useCallback(
    (
      eventKey: string,
      rowKind: RowKind,
      venue: 'kalshi' | 'polymarket',
      side: 'yes' | 'no',
      cents: number,
      meta?: PillTradeMeta,
    ) => {
      // Deliberately a set, never a toggle-off (unlike handleToggleExpand):
      // a pill click carries a price and side; collapsing on re-click would
      // discard the trade intent.
      setExpandedRow({ eventKey, rowKind });

      if (!onTradeClick) return;
      const event = rows.find((r) => r.key === eventKey);
      if (!event) return;
      // A multi-line block click names its OWN line via meta.lineIdx —
      // the selected idx only governs when the click has no line of its own.
      const idx =
        meta?.lineIdx ??
        (rowKind === 'spread'
          ? spreadIdxByEvent.get(eventKey) ?? 0
          : totalIdxByEvent.get(eventKey) ?? 0);
      const line = rowKind === 'spread' ? event.spreads[idx] : event.totals[idx];
      if (!line) return;
      // Follow the click: a neighbor-line trade moves the selection so the
      // panel/chips agree with the market that was just clicked.
      if (meta?.lineIdx != null) {
        if (rowKind === 'spread') handleSelectSpreadIdx(eventKey, meta.lineIdx);
        else handleSelectTotalIdx(eventKey, meta.lineIdx);
      }

      const game =
        rowKind === 'spread'
          ? synthesizeFromSpread(event, line as SpreadLine)
          : synthesizeFromTotal(event, line as TotalLine);

      onTradeClick({
        game,
        teamSide: 'away', // synthetic GameRow has identical away/home books
        venue,
        prefillPrice: cents,
        initialOrderSide: side,
        postOnly: false,
        // Click provenance for the LT2 one-click interception layer only —
        // the ticket ignores it (see TradeTicketContext.meta).
        meta,
      });
    },
    [
      rows,
      spreadIdxByEvent,
      totalIdxByEvent,
      onTradeClick,
      handleSelectSpreadIdx,
      handleSelectTotalIdx,
    ],
  );

  // Named the way the fallback banner and the table title name the board: a
  // bucketed board that says "CFB · FBS" with rows must not call itself plain
  // "CFB" the moment it has none.
  if (rows.length === 0) {
    return (
      <div className="text-muted-foreground flex h-64 flex-col items-center justify-center gap-2">
        {connected ? (
          <span>No {boardLabel ?? sport.toUpperCase()} spreads or totals</span>
        ) : (
          <>
            <div className="border-border border-t-primary h-5 w-5 animate-spin rounded-full border-2" />
            <span>Connecting to Hydra...</span>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      {fallbackSlate && (
        // Above the table, not below it: a reader who met the rows first would
        // have no reason to doubt they are the window the toolbar names.
        <div
          className="border-blue-500/30 bg-blue-500/10 text-blue-200 light:text-blue-700 mb-3 rounded-md border px-3 py-2 text-xs"
        >
          No {boardLabel ?? sport.toUpperCase()} spreads or totals{' '}
          {slateScopeCopy({ startIso: selectedDateIso, endIso: rangeEndIso ?? selectedDateIso }, slateRange)}.{' '}
          {upcomingSlateCopy(fallbackSlate.window, slateRange, 'lines')}
        </div>
      )}
      <SpreadsTotalsTable
        rows={rows}
        boardLabel={boardLabel}
        marketSide={marketSide}
        priceMode={priceMode}
        spreadIdxByEvent={spreadIdxByEvent}
        totalIdxByEvent={totalIdxByEvent}
        expandedRow={expandedRow}
        positionByTicker={userActivity.positionByTicker}
        ordersByTicker={userActivity.displayOrdersByTicker ?? userActivity.ordersByTicker}
        openOrders={openOrders}
        onSelectSpreadIdx={handleSelectSpreadIdx}
        onSelectTotalIdx={handleSelectTotalIdx}
        onToggleExpand={handleToggleExpand}
        onPillTrade={handlePillTrade}
      />
    </>
  );
}

// ── State helpers ─────────────────────────────────────────────────────

/** #780 — this map holds SETTLED selections only: a default chosen against
 *  real prices, or a line the trader (or a #539 focus) picked. A row whose
 *  books carry no price yet has no midline to record, so it is left OUT and
 *  `resolveLineIndices` renders the index-0 fallback for it. The next pass
 *  that does carry prices then seeds it for the first time, on the midline.
 *
 *  Storing the fallback instead is the bug: `defaultFn` had no information,
 *  the ladder end got written down as though it were a real answer, and the
 *  `existing != null` branch below kept it there for the life of the row.
 *  Deriving-until-priced is also what the two sibling surfaces already do —
 *  GameDetail's `SpreadTotalSection` recomputes its default in a memo over
 *  `event.spreads`, and `GameLinesSection`'s `rungKeyByGroup` is written only
 *  by an explicit pick (a rung click or a focus link), never seeded with a
 *  computed default. Its own comment states the rule this restores: "Absent —
 *  or a stored rung no longer listed — falls back to the closest-to-50¢
 *  default." */
function seedLineKeys<T>(
  prev: Map<string, string>,
  rows: GameEventRow[],
  pick: (r: GameEventRow) => T[],
  keyFor: (line: T) => string,
  defaultFn: (lines: T[]) => number | null,
): Map<string, string> {
  let changed = false;
  const next = new Map<string, string>();
  for (const row of rows) {
    const lines = pick(row);
    const existing = prev.get(row.key);
    if (existing != null && lines.some((line) => keyFor(line) === existing)) {
      next.set(row.key, existing);
      continue;
    }
    const idx = defaultFn(lines);
    const line = idx == null ? undefined : lines[idx];
    const defaultKey = line ? keyFor(line) : undefined;
    if (defaultKey) next.set(row.key, defaultKey);
    // Both undefined — an unpriced row that stayed unseeded — is NOT a change,
    // which is what keeps the reference stable and the effect from re-firing.
    if (existing !== defaultKey) changed = true;
  }
  if (!changed && prev.size === next.size) return prev;
  return next;
}

function resolveLineIndices<T>(
  selectedKeys: Map<string, string>,
  rows: GameEventRow[],
  pick: (r: GameEventRow) => T[],
  keyFor: (line: T) => string,
): Map<string, number> {
  const indices = new Map<string, number>();
  for (const row of rows) {
    const lines = pick(row);
    const selectedKey = selectedKeys.get(row.key);
    const selectedIdx = selectedKey == null
      ? -1
      : lines.findIndex((line) => keyFor(line) === selectedKey);
    indices.set(
      row.key,
      selectedIdx >= 0
        ? selectedIdx
        : defaultLineIndex(lines as Array<SpreadLine | TotalLine>),
    );
  }
  return indices;
}

// Re-export shape types for tests / future detail-page reuse.
export type { GameEventRow, SpreadLine, TotalLine };
export type { ExpandedRow, RowKind };
